import { useLocation } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { ImageWithPlaceholder, Typography } from '@wbk/ui';
import { WebsiteConfig } from '@wbk/contentful/api';
import { ContentfulButton } from '@wbk/contentful';
import LanguageSwitch from '@/components/common/LanguageSwitch';
import Copyright from './Copyright';
import Sponsors from './Sponsors';
import Newsletter from './newsletter';

type Props = {
  config: WebsiteConfig;
};

const MainFooter = ({ config }: Props) => {
  //zones page is the only page that doesn't have sponsors in the footer
  // const isZonesPage = window.location.pathname.includes('/zones');

  const { pathname } = useLocation();
  const hideSponsors = pathname.includes('/laylat-alomr');

  return (
    <footer id='footer' className='pt-6'>
      <div className='container space-y-32 px-6 pb-6 md:space-y-40'>
        {!hideSponsors && <Sponsors config={config} />}
        <div>
          <Newsletter />
        </div>

        <div className='border-t border-white/20 md:px-10'>
          <div className='flex flex-col-reverse items-center gap-y-8 py-10 xl:flex-row xl:justify-between xl:space-y-0'>
            <div className='flex w-full flex-col items-center gap-2 lg:items-start'>
              <Typography
                as='p'
                variant='body-M'
                className='text-body-XS text-center font-semibold sm:max-w-xs lg:text-start'
              >
                {config.footer.downloadAppPrompt}
              </Typography>

              <div className='flex items-center md:gap-2'>
                {config.footer.mobileAppLinksCollection.items.map((link) => (
                  <ContentfulButton className='p-1' key={link.id} link={link} />
                ))}
              </div>
            </div>
            <div className='flex w-full grow flex-col items-center justify-between gap-4 space-y-4 lg:flex-row lg:gap-20 lg:space-y-0'>
              <figure className='shrink-0'>
                <ImageWithPlaceholder
                  src={config.footer.logo.url}
                  width={config.footer.logo.width}
                  height={config.footer.logo.height}
                  alt={config.title}
                  className='w-20 object-cover'
                />
              </figure>

              <div className='flex flex-col gap-1'>
                <Typography
                  as='p'
                  variant='body-M'
                  className='text-body-XS text-center font-semibold sm:max-w-xs lg:text-start'
                >
                  {config?.socialLinks?.title}
                </Typography>

                <ul className='flex shrink-0 items-center gap-4'>
                  {config.socialLinks?.iconLinksCollection?.items.map((link) => (
                    <li key={link.id} className='rounded-md'>
                      <ContentfulButton
                        link={link}
                        rel='noreferrer noopener'
                        className='social_media_follow size-6 overflow-hidden rounded-md !p-1 md:size-8'
                        data-location='footer'
                        data-category={link.label}
                        rounded
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className='flex flex-col-reverse items-center justify-center border-t border-gray-500/50 pt-5 lg:flex-row lg:justify-between'>
            <div className='flex flex-col items-center gap-2 lg:items-start'>
              <Typography as='div' variant='body-XS' className='text-center lg:text-start'>
                <Copyright text={config.footer.copyrightText} />
              </Typography>
            </div>

            <nav className='flex flex-col-reverse items-center justify-center gap-4 py-2 pb-8 text-xs sm:flex-row sm:gap-0 lg:p-0'>
              <ul className='flex items-center justify-center gap-2 space-y-0 sm:flex-row'>
                {config.footer.bottomLinksCollection.items.map((link, index) => (
                  <li
                    key={link.id}
                    className={twMerge(
                      'border-white/20 px-4 ltr:border-r rtl:border-l',
                      index === config.footer.bottomLinksCollection.items.length - 1 &&
                        'ltr:border-r-0 sm:ltr:border-r rtl:border-l-0 sm:rtl:border-l'
                    )}
                  >
                    <Typography as='span' variant='body-XS'>
                      <ContentfulButton
                        link={link}
                        rel='noreferrer noopener'
                        data-location='footer'
                        data-category={link.label}
                        className='p-1 px-0 underline-offset-2 hover:underline'
                      />
                    </Typography>
                  </li>
                ))}
              </ul>
              <p className='px-4'>
                <LanguageSwitch withIcon={false} longLang />
              </p>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default MainFooter;
